import React, { ReactElement, RefObject } from "react"

import { TextInput } from "@wfp/ui"
import Select from "react-select"
import {
  CartesianGrid,
  Legend,
  Scatter,
  ScatterChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  TooltipProps,
} from "recharts"

import { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent"
import { formatDate } from "../../utils/helpers"

import { ReportTableData, SimpleSelect } from "../../interfaces/dataInterfaces"

type Props = {
  visualData: {
    report: ReportTableData[]
    selectData: SimpleSelect[]
    series: any
    reportedObs?: ReportTableData
  }
  validationData: ReportTableData[] | undefined
  selectReference: RefObject<any>
  onObsValueChange: (event: any, validationIndex: number, index: number) => void
  onGraphSelectChange: (row?: ReportTableData) => void
}

function ReportVisualDataReview({
  visualData,
  selectReference,
  validationData,
  onObsValueChange,
  onGraphSelectChange,
}: Props): ReactElement {
  const CustomTooltip = ({ payload }: TooltipProps<ValueType, NameType>): ReactElement | null => {
    if (payload?.length) {
      return (
        <div className="border border-solid border-gray-300 rounded-md p-2 bg-white text-xs">
          <p>{`${payload[0]?.name}: ${formatDate(new Date(payload[0]?.value as string).toISOString(), "DD/MM/YY")}`}</p>
          <p>{`${payload[1]?.name}: ${Number(payload[1]?.value)?.toFixed(2)} ${
            visualData?.report[0]?.currencyName
          }`}</p>
        </div>
      )
    }
    return null
  }

  return (
    <>
      <Select
        key={visualData?.selectData[0].value}
        isSearchable
        ref={selectReference}
        className="dropdown mr-4"
        classNamePrefix="select"
        defaultValue={visualData.selectData[0]}
        options={visualData.selectData}
        onChange={event => onGraphSelectChange(validationData?.find(row => row.seriesCombinationCode === event?.value))}
      />

      <div className="grid grid-cols-3 mt-6">
        <div className="col-span-2 mr-6 h-96">
          {visualData.series.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                width={550}
                height={400}
                margin={{
                  top: 20,
                  right: 80,
                  bottom: 20,
                  left: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomTooltip />} />
                <Legend />

                <XAxis
                  style={{ fontSize: "10px" }}
                  tickFormatter={x => formatDate(new Date(x).toISOString(), "DD-MM-YY")}
                  dataKey="x"
                  name="Date"
                  domain={["auto", "auto"]}
                  type="number"
                />
                <YAxis
                  tickFormatter={y => `${y} ${visualData.report[0].currencyName}`}
                  dataKey="y"
                  type="number"
                  style={{ fontSize: "10px" }}
                  label={{ value: "Price", angle: -90, position: "left" }}
                  domain={["dataMin - 50", "auto"]}
                  name="Price"
                />

                {visualData.series.map((s: any) => (
                  <Scatter
                    key={s.name}
                    dataKey="y"
                    isAnimationActive={false}
                    stroke={s.stroke}
                    data={s.data}
                    name={s.name}
                    fill={s.fill}
                    strokeWidth={0}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="col-span-1">
          <div className="compact-table">
            <table className="table text-lg  mt-0">
              <tbody className="custom-tbody">
                <tr>
                  <th className="p-2 font-bold">Row ID</th>
                  <th className="p-2 font-bold">Observation Date</th>
                  <th className="p-2 font-bold">Value</th>
                </tr>

                {visualData.reportedObs?.metadata?.map((meta, index) => (
                  <tr key={meta.rowID}>
                    <td className="p-1 text-center"> {meta.rowID}</td>
                    <td className="p-1 text-center">
                      {formatDate(visualData.reportedObs?.commodityPriceDate || "", "YYYY-MM-DD")}
                    </td>
                    <td className="p-1 text-center">
                      <TextInput
                        type="decimal"
                        value={meta.obsPriceValue || 0}
                        onChange={e => onObsValueChange(e, visualData.reportedObs?.validationIndex as number, index)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

export default ReportVisualDataReview
